import { TenantSelector } from '@/app/[locale]/components/TenantSelector';
import { Avatar } from '@/components/Avatar/Avatar';
import { Icon, IconName } from '@/components/Icon/Icon';
import { useClickOutside } from '@/hooks/useClickOutside';
import { Link, usePathname } from '@/i18n/routing';
import { getInitials } from '@/lib/formatting';
import { cn } from '@/lib/utils';
import Logo from '@/public/img/logo.svg';
import { useLocale, useTranslations } from 'next-intl';
import React, { useRef, useState } from 'react';

const NavigationItem = ({
  className,
  children,
  href,
  icon,
  onClickLink,
}: {
  className?: string;
  children?: React.ReactNode;
  href: string;
  icon?: IconName;
  onClickLink?: () => void;
}) => {
  const pathname = usePathname();

  return (
    <li className={cn('flex justify-center', className)}>
      <Link
        className={cn(
          'block rounded-100 bg-colors-backgrounds-primary p-200 transition-colors hover:bg-buttons-backgrounds-secondary-standard',
          href === pathname && 'bg-buttons-backgrounds-secondary-standard',
        )}
        href={href}
        onClick={onClickLink}
      >
        {icon && <Icon icon={icon} className="h-600 w-600" />}
        {children}
      </Link>
    </li>
  );
};

export const Navigation = ({
  className = '',
  customerName = '',
  isAdmin = false,
  customerId,
  configurationId,
  profilePhotoUrl,
  onClickNavigationItem,
}: {
  className?: string;
  customerName?: string;
  customerId?: string;
  configurationId?: string;
  isAdmin?: boolean;
  profilePhotoUrl?: string;
  onClickNavigationItem?: (label: string) => void;
}) => {
  const logoutRef = useRef<HTMLLIElement>(null);
  const t = useTranslations();
  const locale = useLocale();
  const [showLogout, setShowLogout] = useState(false);
  const showCustomerSection = customerId && customerName;
  useClickOutside(logoutRef, () => {
    setShowLogout(false);
  });

  return (
    <nav
      className={cn(
        'relative z-20 h-screen w-2000 border-r border-colors-borders-secondary bg-colors-backgrounds-glass-low-transparent px-400 py-800 text-colors-content-tertiary',
        className,
      )}
    >
      <ul className="flex size-full flex-col justify-center gap-200">
        <li className="mb-400 flex justify-center">
          <Link
            className="block p-200"
            href="/"
            onClick={() => onClickNavigationItem?.('pitch_general')}
          >
            <span className="block size-[3rem] overflow-hidden rounded-100">
              <Logo className="w-full" />
            </span>
          </Link>
        </li>
        <div className="flex grow flex-col gap-200">
          {isAdmin && (
            <NavigationItem
              href="https://angebot.1komma5grad.com/admin"
              icon="NavGrid"
              onClickLink={() => onClickNavigationItem?.('admin')}
            />
          )}
          <NavigationItem
            href="/customer"
            icon="Customers"
            onClickLink={() => onClickNavigationItem?.('customers')}
          />
          {showCustomerSection && (
            <>
              <hr className="border-t border-colors-borders-secondary" />
              <NavigationItem
                href={`/customer/${customerId}`}
                onClickLink={() => onClickNavigationItem?.('customer_details')}
              >
                <span className="flex h-600 w-600 items-center justify-center font-bold">
                  {getInitials(customerName)}
                </span>
              </NavigationItem>
              {configurationId && (
                <>
                  <NavigationItem
                    onClickLink={() => onClickNavigationItem?.('pitch_custom')}
                    href={`/customer/${customerId}/configuration/${configurationId}/pitch`}
                    icon="Heartbeat"
                  />
                  <NavigationItem
                    onClickLink={() =>
                      onClickNavigationItem?.('configuration_details')
                    }
                    href={`/customer/${customerId}/configuration/${configurationId}`}
                    icon="ShoppingCart"
                  />
                </>
              )}
            </>
          )}

          <li className="relative mt-auto flex justify-center">
            <TenantSelector />
          </li>

          <NavigationItem
            className="mb-400"
            href="/user/profile"
            icon="Settings"
            onClickLink={() => onClickNavigationItem?.('profile')}
          />
        </div>

        <li className="relative" ref={logoutRef}>
          <button onClick={() => setShowLogout((prevValue) => !prevValue)}>
            <Avatar url={profilePhotoUrl} className="h-1200 w-1200" />
          </button>
          {showLogout && (
            <div className="shadow-lg paragraph-small absolute bottom-0 right-0 z-10 -mr-200 mt-100 flex w-2400 translate-x-full flex-col overflow-hidden rounded-200 border border-colors-borders-secondary bg-white">
              <a
                href={`/api/auth/logout?locale=${locale}&logout=true`}
                className="w-full px-400 py-300 text-left transition-colors hover:bg-buttons-backgrounds-secondary-hover"
              >
                {t('navigation_logout')}
              </a>
            </div>
          )}
        </li>
      </ul>
    </nav>
  );
};
